import { useEffect, useState } from "react";
import {
  Mail, Webhook, Code2, Users as UsersIcon, Save, CheckCircle2, AlertCircle,
  Loader2, Send, Plus, Trash2, KeyRound, Edit3, X,
} from "lucide-react";
import { accessApi } from "@/access/api";

const TABS = [
  { id: "smtp",     label: "SMTP · Email",  Icon: Mail },
  { id: "webhooks", label: "Webhooks",      Icon: Webhook },
  { id: "scripts",  label: "Scripts & SEO", Icon: Code2 },
  { id: "team",     label: "Team",          Icon: UsersIcon },
];

const WEBHOOK_FIELDS = [
  { key: "webhook_default",            label: "General webhook (fallback)", hint: "Fires for every submission" },
  { key: "webhook_lead",               label: "Lead / demo requests",        hint: "/api/leads" },
  { key: "webhook_build_os",           label: "Build OS submissions",        hint: "Plan builder — base · add-ons · whitelabel" },
  { key: "webhook_audit",              label: "Clinic audit",                hint: "/api/audit" },
  { key: "webhook_integration_notify", label: "EMR integration notify",      hint: "/api/integrations/notify" },
  { key: "webhook_newsletter",         label: "Newsletter signup",           hint: "/api/newsletter/subscribe" },
  { key: "webhook_support",            label: "Support / contact form",      hint: "Support page form" },
];

export default function AccessSettings() {
  const [tab, setTab] = useState(() => {
    if (typeof window !== "undefined") {
      const fromHash = window.location.hash.replace("#", "");
      if (TABS.some((t) => t.id === fromHash)) return fromHash;
    }
    return "smtp";
  });

  useEffect(() => {
    if (typeof window !== "undefined") window.location.hash = tab;
  }, [tab]);

  return (
    <div data-testid="access-settings" className="space-y-6">
      <header>
        <p className="text-[10.5px] font-bold uppercase tracking-[0.32em] text-violet-300">Settings</p>
        <h1 className="mt-1.5 font-display text-3xl font-black tracking-tight sm:text-4xl">Workspace settings</h1>
        <p className="mt-1.5 text-[13.5px] text-white/55">
          Configure email delivery, webhooks, scripts and team members.
        </p>
      </header>

      {/* Tabs */}
      <nav className="-mx-1 flex gap-1.5 overflow-x-auto rounded-2xl border border-white/[0.06] bg-white/[0.02] p-1.5 [&::-webkit-scrollbar]:hidden">
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            data-testid={`settings-tab-${id}`}
            className={`inline-flex flex-none items-center gap-2 rounded-xl px-3.5 py-2 text-[12.5px] font-bold transition-all whitespace-nowrap ${
              tab === id
                ? "bg-gradient-to-r from-violet-600/30 to-fuchsia-600/20 text-white shadow-inner ring-1 ring-inset ring-white/10"
                : "text-white/55 hover:bg-white/[0.04] hover:text-white"
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </nav>

      <div className="min-h-[420px]">
        {tab === "smtp" && <SmtpTab />}
        {tab === "webhooks" && <WebhooksTab />}
        {tab === "scripts" && <ScriptsTab />}
        {tab === "team" && <TeamTab />}
      </div>
    </div>
  );
}

/* ===================== SMTP TAB ===================== */
function SmtpTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ ok: "", err: "" });
  const [test, setTest] = useState({ to: "", result: null, error: "" });
  const [recipientsRaw, setRecipientsRaw] = useState("");

  useEffect(() => {
    accessApi.settings().then(({ data }) => {
      setS(data);
      setRecipientsRaw((data.smtp_internal_recipients || []).join(", "));
    });
  }, []);

  if (!s) return <SectionLoading />;

  const update = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    setMsg({ ok: "", err: "" });
    try {
      const body = {
        ...s,
        smtp_internal_recipients: recipientsRaw.split(",").map((x) => x.trim()).filter(Boolean),
      };
      delete body.smtp_password_set;
      if (!s.smtp_password) delete body.smtp_password;
      const { data } = await accessApi.saveSettings(body);
      setS(data);
      setMsg({ ok: "SMTP settings saved.", err: "" });
      setTimeout(() => setMsg({ ok: "", err: "" }), 2500);
    } catch (e) {
      setMsg({ ok: "", err: e?.response?.data?.detail || "Save failed." });
    } finally {
      setSaving(false);
    }
  };

  const doTest = async () => {
    if (!test.to) return;
    setTest((t) => ({ ...t, result: "loading", error: "" }));
    try {
      const { data } = await accessApi.testSmtp(test.to);
      setTest((t) => ({
        ...t,
        result: data.ok ? "ok" : "fail",
        error: data.ok ? "" : (data.error || "Unknown error"),
      }));
    } catch (e) {
      setTest((t) => ({
        ...t, result: "fail",
        error: e?.response?.data?.detail || e.message || "Network error",
      }));
    }
  };

  return (
    <SectionCard title="SMTP · Google Workspace" subtitle="Connect Gmail / Workspace so every submission triggers customer + internal emails.">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="SMTP host"><Input value={s.smtp_host || ""} onChange={(v) => update("smtp_host", v)} placeholder="smtp.gmail.com" /></Field>
        <Field label="Port"><Input value={s.smtp_port || ""} onChange={(v) => update("smtp_port", Number(v) || 587)} placeholder="587" /></Field>
        <Field label="SMTP user"><Input value={s.smtp_user || ""} onChange={(v) => update("smtp_user", v)} placeholder="ops@clinicfirst.app" /></Field>
        <Field label="Password">
          <div className="relative">
            <Input
              type="password"
              value={s.smtp_password || ""}
              onChange={(v) => update("smtp_password", v)}
              placeholder={s.smtp_password_set ? "•••••••• Saved · type to replace" : "Paste Google App Password"}
            />
            {s.smtp_password_set && !s.smtp_password && (
              <span className="pointer-events-none absolute right-3 top-1/2 inline-flex -translate-y-1/2 items-center gap-1 rounded-md bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-300">
                <CheckCircle2 className="h-3 w-3" />
                Saved
              </span>
            )}
          </div>
          {s.smtp_password_set && !s.smtp_password && (
            <p className="mt-1.5 text-[11px] text-emerald-300/85">
              Password is saved and being used. Leave blank to keep — type a new one to replace it.
            </p>
          )}
          {!s.smtp_password_set && (
            <p className="mt-1.5 text-[11px] text-amber-300/85">
              Use a Google <strong>App Password</strong> (not your login password). Generate at myaccount.google.com → Security → 2-Step → App passwords.
            </p>
          )}
        </Field>
        <Field label="From name"><Input value={s.smtp_from_name || ""} onChange={(v) => update("smtp_from_name", v)} placeholder="Clinic OS" /></Field>
        <Field label="From email"><Input value={s.smtp_from_email || ""} onChange={(v) => update("smtp_from_email", v)} placeholder="hello@clinicfirst.app" /></Field>
        <Field label="Reply-To"><Input value={s.smtp_reply_to || ""} onChange={(v) => update("smtp_reply_to", v)} placeholder="sales@clinicfirst.app" /></Field>
        <Field label="Use STARTTLS">
          <select
            value={s.smtp_use_tls ? "yes" : "no"}
            onChange={(e) => update("smtp_use_tls", e.target.value === "yes")}
            className="block w-full rounded-lg border border-white/[0.1] bg-white/[0.04] px-3 py-2.5 text-[13px] focus:border-violet-400/60 focus:outline-none"
          >
            <option value="yes" className="bg-[#0a0a14]">Yes (recommended)</option>
            <option value="no" className="bg-[#0a0a14]">No</option>
          </select>
        </Field>
        <Field label="Internal recipients (comma-separated)" full>
          <Input value={recipientsRaw} onChange={setRecipientsRaw} placeholder="sales@clinicfirst.app, ops@clinicfirst.app" />
          <p className="mt-1.5 text-[10.5px] text-white/40">Each address receives an internal notification email for every form submission.</p>
        </Field>
      </div>

      <div className="mt-6 rounded-xl border border-white/[0.08] bg-white/[0.02] p-3">
        <div className="flex flex-wrap items-center gap-3">
          <Input value={test.to} onChange={(v) => setTest((t) => ({ ...t, to: v }))} placeholder="Send a test email to…" slim />
          <button
            onClick={doTest}
            data-testid="settings-test-smtp"
            className="inline-flex items-center gap-1.5 rounded-lg border border-violet-400/40 bg-violet-500/10 px-3 py-1.5 text-[12px] font-bold text-violet-200 hover:bg-violet-500/20"
          >
            {test.result === "loading" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Test SMTP
          </button>
          {test.result === "ok"   && <span className="text-[12px] font-bold text-emerald-300">✓ Sent — check the inbox.</span>}
          {test.result === "fail" && <span className="text-[12px] font-bold text-rose-300">✗ Failed</span>}
        </div>
        {test.result === "fail" && test.error && (
          <p className="mt-2 rounded-md border border-rose-500/30 bg-rose-500/[0.06] p-2.5 font-mono text-[11.5px] leading-relaxed text-rose-200">
            {test.error}
          </p>
        )}
      </div>

      <SaveBar saving={saving} msg={msg} onSave={save} />
    </SectionCard>
  );
}

/* ===================== WEBHOOKS TAB ===================== */
function WebhooksTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ ok: "", err: "" });
  const [test, setTest] = useState({ url: "", result: null });

  useEffect(() => { accessApi.settings().then(({ data }) => setS(data)); }, []);
  if (!s) return <SectionLoading />;

  const update = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    setMsg({ ok: "", err: "" });
    try {
      const { data } = await accessApi.saveSettings(s);
      setS(data);
      setMsg({ ok: "Webhooks saved.", err: "" });
      setTimeout(() => setMsg({ ok: "", err: "" }), 2500);
    } catch (e) {
      setMsg({ ok: "", err: e?.response?.data?.detail || "Save failed." });
    } finally { setSaving(false); }
  };

  const doTest = async () => {
    if (!test.url) return;
    setTest((t) => ({ ...t, result: "loading" }));
    try {
      const { data } = await accessApi.testWebhook(test.url);
      setTest((t) => ({ ...t, result: data.ok ? "ok" : "fail" }));
    } catch { setTest((t) => ({ ...t, result: "fail" })); }
  };

  return (
    <SectionCard title="Webhooks · per-form & general" subtitle="Each form fires its per-form webhook (if set) and the general webhook. Both receive the full submission JSON plus attribution.">
      <div className="space-y-3">
        {WEBHOOK_FIELDS.map((f) => (
          <div key={f.key} className="grid items-center gap-2 sm:grid-cols-[260px,1fr]">
            <div>
              <p className="font-display text-[13px] font-black tracking-tight">{f.label}</p>
              <p className="text-[11px] text-white/45">{f.hint}</p>
            </div>
            <Input value={s[f.key] || ""} onChange={(v) => update(f.key, v)} placeholder="https://services.leadconnectorhq.com/hooks/…" />
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3 rounded-xl border border-white/[0.08] bg-white/[0.02] p-3">
        <Input value={test.url} onChange={(v) => setTest((t) => ({ ...t, url: v }))} placeholder="Test any webhook URL…" slim />
        <button
          onClick={doTest}
          data-testid="settings-test-webhook"
          className="inline-flex items-center gap-1.5 rounded-lg border border-violet-400/40 bg-violet-500/10 px-3 py-1.5 text-[12px] font-bold text-violet-200 hover:bg-violet-500/20"
        >
          {test.result === "loading" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          Fire test
        </button>
        {test.result === "ok"   && <span className="text-[12px] text-emerald-300">✓ Webhook returned OK</span>}
        {test.result === "fail" && <span className="text-[12px] text-rose-300">✗ Failed</span>}
      </div>

      <SaveBar saving={saving} msg={msg} onSave={save} />
    </SectionCard>
  );
}

/* ===================== SCRIPTS TAB ===================== */
function ScriptsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ ok: "", err: "" });

  useEffect(() => { accessApi.settings().then(({ data }) => setS(data)); }, []);
  if (!s) return <SectionLoading />;

  const update = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    setMsg({ ok: "", err: "" });
    try {
      const { data } = await accessApi.saveSettings(s);
      setS(data);
      setMsg({ ok: "Scripts saved.", err: "" });
      setTimeout(() => setMsg({ ok: "", err: "" }), 2500);
    } catch (e) {
      setMsg({ ok: "", err: e?.response?.data?.detail || "Save failed." });
    } finally { setSaving(false); }
  };

  return (
    <SectionCard title="Scripts · GTM · GA4 · custom" subtitle="Injected on every public page through the Layout. Changes apply on next site load.">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="GTM container ID"><Input value={s.gtm_id || ""} onChange={(v) => update("gtm_id", v)} placeholder="GTM-XXXXXX" /></Field>
        <Field label="GA4 measurement ID"><Input value={s.ga4_id || ""} onChange={(v) => update("ga4_id", v)} placeholder="G-XXXXXXXX" /></Field>
      </div>
      <Field label="Custom <head> scripts (raw HTML / JS)" full>
        <textarea
          value={s.head_scripts || ""}
          onChange={(e) => update("head_scripts", e.target.value)}
          rows={5}
          className="block w-full resize-y rounded-lg border border-white/[0.1] bg-white/[0.04] px-3 py-2.5 font-mono text-[12px] outline-none focus:border-violet-400/60"
          placeholder="<!-- HotJar, Segment, custom analytics, etc. -->"
        />
      </Field>
      <Field label="Custom end-of-<body> scripts" full>
        <textarea
          value={s.body_scripts || ""}
          onChange={(e) => update("body_scripts", e.target.value)}
          rows={4}
          className="block w-full resize-y rounded-lg border border-white/[0.1] bg-white/[0.04] px-3 py-2.5 font-mono text-[12px] outline-none focus:border-violet-400/60"
          placeholder="<!-- Intercom widget, Drift chat, etc. -->"
        />
      </Field>
      <SaveBar saving={saving} msg={msg} onSave={save} />
    </SectionCard>
  );
}

/* ===================== TEAM TAB ===================== */
function TeamTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", role: "admin" });
  const [msg, setMsg] = useState({ ok: "", err: "" });
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: "", password: "", role: "" });

  const load = () => accessApi.users().then(({ data }) => setUsers(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setMsg({ ok: "", err: "" });
    setAdding(true);
    try {
      await accessApi.createUser(newUser);
      setNewUser({ email: "", name: "", password: "", role: "admin" });
      setMsg({ ok: "Team member added.", err: "" });
      load();
      setTimeout(() => setMsg({ ok: "", err: "" }), 2500);
    } catch (e) {
      setMsg({ ok: "", err: e?.response?.data?.detail || "Failed to add user." });
    } finally { setAdding(false); }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setEditDraft({ name: u.name || "", password: "", role: u.role || "admin" });
  };

  const saveEdit = async (uid) => {
    const body = {};
    if (editDraft.name) body.name = editDraft.name;
    if (editDraft.password) body.password = editDraft.password;
    if (editDraft.role) body.role = editDraft.role;
    try {
      await accessApi.updateUser(uid, body);
      setEditingId(null);
      setMsg({ ok: "Updated.", err: "" });
      load();
      setTimeout(() => setMsg({ ok: "", err: "" }), 2500);
    } catch (e) {
      setMsg({ ok: "", err: e?.response?.data?.detail || "Failed to update." });
    }
  };

  const remove = async (uid, email) => {
    if (!window.confirm(`Remove ${email}? They will lose access immediately.`)) return;
    try {
      await accessApi.deleteUser(uid);
      load();
    } catch (e) {
      setMsg({ ok: "", err: e?.response?.data?.detail || "Failed to remove." });
    }
  };

  return (
    <SectionCard title="Team members" subtitle="Anyone with an account here can sign in to /access. Each user has independent credentials.">
      {loading ? <SectionLoading /> : (
        <ul className="space-y-2">
          {users.map((u) => (
            <li
              key={u.id}
              data-testid={`team-row-${u.email}`}
              className="rounded-xl border border-white/[0.08] bg-white/[0.025] p-4"
            >
              {editingId === u.id ? (
                <div className="space-y-3">
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Field label="Name">
                      <Input value={editDraft.name} onChange={(v) => setEditDraft((d) => ({ ...d, name: v }))} />
                    </Field>
                    <Field label="Role">
                      <Input value={editDraft.role} onChange={(v) => setEditDraft((d) => ({ ...d, role: v }))} />
                    </Field>
                    <Field label="New password (optional)">
                      <Input type="password" value={editDraft.password} onChange={(v) => setEditDraft((d) => ({ ...d, password: v }))} placeholder="Leave blank to keep" />
                    </Field>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="inline-flex items-center gap-1 rounded-md border border-white/[0.1] bg-white/[0.04] px-3 py-1.5 text-[11.5px] font-bold text-white/70 hover:bg-white/[0.08]"
                    >
                      <X className="h-3 w-3" /> Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => saveEdit(u.id)}
                      data-testid={`team-save-${u.email}`}
                      className="inline-flex items-center gap-1 rounded-md bg-gradient-to-r from-violet-600 to-fuchsia-600 px-3 py-1.5 text-[11.5px] font-bold text-white hover:-translate-y-0.5"
                    >
                      <Save className="h-3 w-3" /> Save
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 font-display text-sm font-black uppercase text-white shadow-sm">
                      {(u.name || u.email || "?")[0]}
                    </span>
                    <div className="min-w-0">
                      <p className="font-display text-[14px] font-black truncate">
                        {u.name || "—"}
                        {u.is_self && (
                          <span className="ml-2 inline-flex rounded bg-violet-500/20 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.14em] text-violet-200">You</span>
                        )}
                      </p>
                      <p className="text-[11.5px] text-white/55 truncate">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="hidden sm:inline-flex rounded-md bg-white/[0.06] px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-white/65">
                      {u.role || "admin"}
                    </span>
                    <button
                      type="button"
                      onClick={() => startEdit(u)}
                      data-testid={`team-edit-${u.email}`}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-white/[0.1] bg-white/[0.03] text-white/65 hover:bg-white/[0.06] hover:text-white"
                      aria-label="Edit"
                    >
                      <Edit3 className="h-3.5 w-3.5" />
                    </button>
                    {!u.is_self && (
                      <button
                        type="button"
                        onClick={() => remove(u.id, u.email)}
                        data-testid={`team-delete-${u.email}`}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-rose-500/25 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20"
                        aria-label="Remove"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={create} className="mt-6 rounded-2xl border border-violet-500/25 bg-violet-500/[0.04] p-5">
        <p className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.22em] text-violet-300">
          <Plus className="h-3.5 w-3.5" /> Add team member
        </p>
        <p className="mt-1 text-[12px] text-white/45">They&apos;ll be able to sign in immediately with the email + password you set.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Field label="Email">
            <Input type="email" value={newUser.email} onChange={(v) => setNewUser((u) => ({ ...u, email: v }))} placeholder="teammate@clinicfirst.app" required />
          </Field>
          <Field label="Name">
            <Input value={newUser.name} onChange={(v) => setNewUser((u) => ({ ...u, name: v }))} placeholder="Sam Reception" />
          </Field>
          <Field label="Password (≥ 8 chars)">
            <Input type="password" value={newUser.password} onChange={(v) => setNewUser((u) => ({ ...u, password: v }))} placeholder="Generate or enter" />
          </Field>
          <Field label="Role">
            <Input value={newUser.role} onChange={(v) => setNewUser((u) => ({ ...u, role: v }))} placeholder="admin / sales / support" />
          </Field>
        </div>
        <div className="mt-4 flex items-center justify-end gap-3">
          {msg.ok && <span className="text-[12px] font-bold text-emerald-300">✓ {msg.ok}</span>}
          {msg.err && <span className="text-[12px] font-bold text-rose-300">{msg.err}</span>}
          <button
            type="submit"
            disabled={adding}
            data-testid="team-add-submit"
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 px-4 py-2 text-[12.5px] font-bold text-white shadow-md disabled:opacity-60"
          >
            {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Add member
          </button>
        </div>
      </form>
    </SectionCard>
  );
}

/* ---------------- shared bits ---------------- */
const SectionCard = ({ title, subtitle, children }) => (
  <section className="rounded-2xl border border-white/[0.06] bg-white/[0.025] p-5 sm:p-6">
    <div className="mb-5">
      <p className="font-display text-lg font-black tracking-tight sm:text-xl">{title}</p>
      {subtitle && <p className="mt-1 text-[12.5px] text-white/55">{subtitle}</p>}
    </div>
    {children}
  </section>
);

const SectionLoading = () => (
  <div className="flex items-center justify-center gap-2 py-16 text-[12px] text-white/40">
    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
  </div>
);

const Field = ({ label, full, children }) => (
  <label className={`block ${full ? "mt-4 sm:col-span-2" : ""}`}>
    <span className="mb-1.5 inline-block text-[10.5px] font-bold uppercase tracking-[0.22em] text-white/55">{label}</span>
    {children}
  </label>
);

const Input = ({ value, onChange, placeholder, type = "text", slim, required }) => (
  <input
    type={type}
    required={required}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className={`block w-full rounded-lg border border-white/[0.1] bg-white/[0.04] ${slim ? "px-3 py-1.5 text-[12px]" : "px-3 py-2.5 text-[13px]"} text-white outline-none placeholder:text-white/30 focus:border-violet-400/60`}
  />
);

const SaveBar = ({ saving, msg, onSave }) => (
  <div className="mt-6 flex items-center justify-end gap-3 border-t border-white/[0.06] pt-4">
    {msg.ok && (
      <span className="inline-flex items-center gap-1.5 text-[12px] font-bold text-emerald-300">
        <CheckCircle2 className="h-3.5 w-3.5" /> {msg.ok}
      </span>
    )}
    {msg.err && (
      <span className="inline-flex items-center gap-1.5 text-[12px] font-bold text-rose-300">
        <AlertCircle className="h-3.5 w-3.5" /> {msg.err}
      </span>
    )}
    <button
      onClick={onSave}
      disabled={saving}
      data-testid="settings-save"
      className="inline-flex h-10 items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-5 text-[13px] font-bold text-white shadow-md disabled:opacity-60"
    >
      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
      {saving ? "Saving…" : "Save changes"}
    </button>
  </div>
);
